import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { milestones, testimonials, team } from '@/mocks/about';
import { useState, FormEvent } from 'react';

const teamAvatarImages: Record<string, string> = {
  '张明远': 'https://readdy.ai/api/search-image?query=Senior%20Asian%20male%20executive%20professional%20portrait%20in%20tailored%20suit%20confident%20expression%20warm%20corporate%20headshot&width=300&height=300&seq=team-avatar-1&orientation=portrait',
  '李婉清': 'https://readdy.ai/api/search-image?query=Professional%20Asian%20female%20executive%20portrait%20in%20modern%20business%20attire%20elegant%20confident%20warm%20lighting%20headshot&width=300&height=300&seq=team-avatar-2&orientation=portrait',
  'James Chen': 'https://readdy.ai/api/search-image?query=Asian%20male%20executive%20in%20international%20business%20setting%20professional%20portrait%20confident%20expression%20warm%20corporate%20headshot&width=300&height=300&seq=team-avatar-3&orientation=portrait',
  '赵安琪': 'https://readdy.ai/api/search-image?query=Asian%20female%20creative%20director%20professional%20portrait%20artistic%20yet%20corporate%20warm%20lighting%20modern%20headshot&width=300&height=300&seq=team-avatar-4&orientation=portrait',
};

const testimonialAvatarImages: Record<string, string> = {
  '王志远': 'https://readdy.ai/api/search-image?query=Asian%20male%20CEO%20professional%20portrait%20in%20suit%20warm%20lighting%20executive%20headshot&width=200&height=200&seq=testimonial-1&orientation=squarish',
  '李明月': 'https://readdy.ai/api/search-image?query=Asian%20female%20business%20executive%20professional%20portrait%20confident%20warm%20lighting%20corporate%20headshot&width=200&height=200&seq=testimonial-2&orientation=squarish',
  '陈大卫': 'https://readdy.ai/api/search-image?query=Asian%20male%20entrepreneur%20professional%20portrait%20in%20casual%20blazer%20warm%20lighting%20modern%20headshot&width=200&height=200&seq=testimonial-3&orientation=squarish',
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formSubmitAddr = 'https://readdy.ai/api/form/d9u4sgtca23r0tr1aqkg';

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch(formSubmitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="征途国际成立于2015年，汇聚国际化领域资深专家，致力于为中国企业提供全球市场拓展、跨境合作、资源对接等一站式国际化服务。"
        keywords="征途国际关于我们,国际化服务公司,全球市场拓展团队,跨境业务合作,征途国际品牌"
        canonicalUrl="https://exportr.github.io/about"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            'name': '征途国际 - 关于我们',
            'description': '征途国际成立于2015年，致力于为中国企业提供一站式国际化服务。',
            'url': 'https://exportr.github.io/about',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            'itemListElement': [
              { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://exportr.github.io' },
              { '@type': 'ListItem', 'position': 2, 'name': '关于我们', 'item': 'https://exportr.github.io/about' },
            ],
          },
        ]}
      />

      <Navbar />
      <ScrollToTop />

      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />

        <section className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1">
                <span className="text-xs font-semibold tracking-widest uppercase text-primary-500 mb-3 block">关于征途国际</span>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-5">
                  全球征途，无限可能
                </h1>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  征途国际成立于2015年，总部位于北京，是一家专注于为中国企业提供一站式国际化服务的专业机构。我们的使命是助力中国企业走向世界，在全球市场中实现更大价值。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  我们的核心团队汇聚了来自麦肯锡、阿里巴巴、汇丰银行、奥美等全球顶级机构的资深专家，在战略咨询、跨境合作、品牌国际化、数字全球化等领域拥有丰富的实战经验。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  截至目前，征途国际已累计服务超过500家企业，服务网络覆盖全球50多个国家和地区，在行业内建立了卓越的口碑和品牌影响力。
                </p>
              </div>
              <div className="flex-1">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20corporate%20office%20headquarters%20with%20world%20map%20wall%20art%20professional%20Chinese%20business%20atmosphere%20warm%20elegant%20interior%20design&width=700&height=500&seq=about-hero&orientation=landscape"
                  alt="征途国际总部"
                  className="w-full rounded-lg object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-100/70">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-primary-500 mb-3 block">发展历程</span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                征途十年，步履不停
              </h2>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-primary-200/60 md:-translate-x-px" />

              <div className="flex flex-col gap-8">
                {milestones.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex flex-col md:flex-row gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  >
                    <div className="flex-1 md:text-right">
                      <div className={`bg-background-50 border border-background-200/60 rounded-lg p-5 ${index % 2 !== 0 ? 'md:text-left' : ''}`}>
                        <span className="text-xs font-bold text-primary-500">{item.year}</span>
                        <h3 className="text-sm font-semibold text-foreground-950 mt-1 mb-2 font-heading">{item.title}</h3>
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex flex-shrink-0 w-10 items-start justify-center pt-5">
                      <div className="w-4 h-4 rounded-full bg-primary-500 border-4 border-background-50" />
                    </div>
                    <div className="flex-1 hidden md:block" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-primary-500 mb-3 block">核心团队</span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                资深专家，全球视野
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {team.map((member) => (
                <div
                  key={member.name}
                  className="bg-background-100/70 border border-background-200/60 rounded-lg p-6 text-center"
                >
                  <img
                    src={teamAvatarImages[member.name]}
                    alt={member.name}
                    className="w-24 h-24 rounded-full object-cover mx-auto mb-4"
                  />
                  <h3 className="text-sm font-semibold text-foreground-950 font-heading">{member.name}</h3>
                  <p className="text-xs text-primary-600 mb-3">{member.title}</p>
                  <p className="text-xs text-foreground-600 leading-relaxed">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-100/70">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-primary-500 mb-3 block">客户口碑</span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                客户的成功，我们的骄傲
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="bg-background-50 border border-background-200/60 rounded-lg p-6"
                >
                  <div className="w-8 h-8 mb-4 flex items-center justify-center">
                    <i className="ri-double-quotes-l text-2xl text-primary-300"></i>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-5 italic">
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={testimonialAvatarImages[t.name]}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-xs font-semibold text-foreground-950">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.title}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-primary-500 mb-3 block">联系我们</span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                开启您的全球征途
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto leading-relaxed">
                无论您有什么国际化需求，我们的专业团队都随时准备为您服务
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-6 font-heading">
                  联系方式
                </h3>
                <div className="flex flex-col gap-5">
                  <div className="flex items-start gap-4">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center bg-primary-100/80 rounded-lg">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-950">电话</div>
                      <div className="text-sm text-foreground-600">400-888-9999</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center bg-primary-100/80 rounded-lg">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-950">邮箱</div>
                      <div className="text-sm text-foreground-600">contact@zhengtuguoji.com</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center bg-primary-100/80 rounded-lg">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-950">地址</div>
                      <div className="text-sm text-foreground-600">北京市朝阳区建国路88号SOHO现代城A座25层</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center bg-primary-100/80 rounded-lg">
                      <i className="ri-time-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-950">工作时间</div>
                      <div className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</div>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h3 className="text-base font-semibold text-foreground-950 mb-4 font-heading">我们的位置</h3>
                  <div className="rounded-lg overflow-hidden border border-background-200/60">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12223.544467753442!2d116.4601044!3d39.9049899!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f052e705fe0f01%3A0x94d2f80cbf0d0f51!2sSOHO%20Modern%20City%2C%20Chaoyang%2C%20Beijing!5e0!3m2!1sen!2scn!4v1!5m2!1sen!2scn"
                      width="100%"
                      height="280"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="征途国际地址"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-6 font-heading">
                  发送消息
                </h3>
                {formStatus === 'success' ? (
                  <div className="bg-accent-50 border border-accent-200/60 rounded-lg p-8 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-accent-100/80 rounded-full">
                      <i className="ri-check-line text-2xl text-accent-600"></i>
                    </div>
                    <h4 className="text-base font-semibold text-foreground-950 mb-2 font-heading">提交成功！</h4>
                    <p className="text-sm text-foreground-600">
                      感谢您的咨询，我们的专业顾问将在24小时内与您联系。
                    </p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="mt-6 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
                    >
                      发送新消息
                    </button>
                  </div>
                ) : (
                  <form className="flex flex-col gap-4" data-readdy-form="true" onSubmit={handleSubmit}>
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm border border-background-200/60 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400/60 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 text-sm border border-background-200/60 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400/60 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-company" className="block text-xs font-medium text-foreground-700 mb-1.5">公司名称</label>
                      <input
                        id="contact-company"
                        name="company"
                        type="text"
                        className="w-full px-4 py-2.5 text-sm border border-background-200/60 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400/60 transition-colors"
                        placeholder="请输入您的公司名称"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">咨询内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm border border-background-200/60 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400/60 transition-colors resize-none"
                        placeholder="请描述您的国际化需求或咨询问题（500字以内）"
                      />
                    </div>
                    <div className="website-alt-wrapper">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    {formError && (
                      <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-2.5">
                        {formError}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'loading' ? '提交中...' : '提交咨询'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}